import { useEffect, useState } from 'react';
import { Spinner, useToast } from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ROLE_HELP, ROLE_LABEL, type Role, type User } from '../lib/types';

const ROLES: Role[] = ['editor', 'reviewer', 'viewer', 'admin'];

export function UsersPage() {
  const { user: me } = useAuth();
  const toast = useToast();
  const [users, setUsers] = useState<User[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ username: '', name: '', role: 'editor' as Role, password: '' });

  const load = () => api.listUsers().then(setUsers).catch((e) => toast.push(e.message, true));
  useEffect(() => { void load(); }, []);

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      toast.push(ok);
      await load();
    } catch (e) {
      toast.push((e as Error).message, true);
    }
  };

  const add = () => run(async () => {
    await api.createUser(form);
    setForm({ username: '', name: '', role: 'editor', password: '' });
    setAdding(false);
  }, 'User added');

  if (!users) return <div className="row"><Spinner /> <span className="sub">Loading…</span></div>;

  return (
    <>
      <div className="row" style={{ marginBottom: 18 }}>
        <div>
          <h1 className="title">Users</h1>
          <div className="sub">Who can sign in, and what they may do.</div>
        </div>
        <div style={{ flex: 1 }} />
        <button className="btn primary" onClick={() => setAdding((v) => !v)}>+ Add user</button>
      </div>

      {adding && (
        <div className="panel" style={{ maxWidth: 720 }}>
          <div className="grid-2">
            <div className="field">
              <label>Username</label>
              <input value={form.username} autoFocus placeholder="andi" onChange={(e) => setForm({ ...form, username: e.target.value })} />
            </div>
            <div className="field">
              <label>Full name</label>
              <input value={form.name} placeholder="Andi Pratama" onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="field">
              <label>Role</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as Role })}>
                {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]} — {ROLE_HELP[r]}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Temporary password</label>
              <input value={form.password} placeholder="at least 8 characters" onChange={(e) => setForm({ ...form, password: e.target.value })} />
              <div className="hint">Share it with them; they can change it under “Your account”.</div>
            </div>
          </div>
          <div className="row end">
            <button className="btn ghost" onClick={() => setAdding(false)}>Cancel</button>
            <button className="btn primary" disabled={!form.username || form.password.length < 8} onClick={() => void add()}>Add user</button>
          </div>
        </div>
      )}

      <div className="panel" style={{ padding: 0 }}>
        <table className="table plain">
          <thead>
            <tr><th>Name</th><th>Username</th><th>Role</th><th>Status</th><th /></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>{u.name}{u.id === me?.id && <span className="sub"> (you)</span>}</td>
                <td className="sub">@{u.username}</td>
                <td>
                  <select
                    className="inline-select"
                    value={u.role}
                    disabled={u.id === me?.id}
                    onChange={(e) => void run(() => api.updateUser(u.id, { role: e.target.value as Role }), 'Role changed')}
                  >
                    {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                  </select>
                </td>
                <td>{u.disabled ? <span className="pill REVISION">Disabled</span> : <span className="pill APPROVED">Active</span>}</td>
                <td>
                  <div className="row end" style={{ gap: 6 }}>
                    <button
                      className="btn small"
                      onClick={() => {
                        const password = window.prompt(`New password for ${u.name} (at least 8 characters)`);
                        if (password) void run(() => api.updateUser(u.id, { password }), 'Password reset');
                      }}
                    >Reset password</button>
                    {u.id !== me?.id && (
                      <button className="btn small" onClick={() => void run(() => api.updateUser(u.id, { disabled: !u.disabled }), u.disabled ? 'Enabled' : 'Disabled')}>
                        {u.disabled ? 'Enable' : 'Disable'}
                      </button>
                    )}
                    {u.id !== me?.id && (
                      <button
                        className="btn small danger"
                        onClick={() => window.confirm(`Delete ${u.name}? Their comments and history stay.`) && void run(() => api.deleteUser(u.id), 'User deleted')}
                      >Delete</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
